// PathBadge — persistent "your path" indicator that lives in SiteHeader.
//
// A reader who completes the persona engine on the home page receives a
// reading prescription. Without this badge, navigation away from About
// loses the prescription's place — the reader has to find the persona
// engine again to remember what's next. The badge is the prescription's
// visible afterlife: it keeps a quiet thread to the reader's next step
// across every page.
//
// Three states:
//   1. No path — render null. The badge is invisible until About.tsx
//      writes localStorage["ktp.path.v1"] (canonical schema below).
//   2. Mid-path (step < prescription.length) — small mono kicker
//      "your path", persona label, → currentItem.title, "next →" button.
//      Clicking next bumps step in localStorage and navigates.
//   3. Complete (step === prescription.length) — "complete ✓" state with
//      a tiny dismiss/reset that clears the key.
//
// Mobile (<720px): collapses to icon-only ("→") so it doesn't crowd the
// hamburger. Tap navigates direct to the next item.
//
// Reads localStorage on mount + listens for `storage` events so the
// badge updates across tabs (and within the same tab via a custom
// dispatched StorageEvent when next is clicked).
//
// All reads are defensive: any malformed payload returns null so the
// nav never crashes on a stale or hand-edited storage entry.

import { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "wouter";

const PATH_KEY = "ktp.path.v1";

type PathItem = {
  title: string;
  href: string;
  minutes?: number;
  external?: boolean;
};

type PathState = {
  persona: string;
  personaLabel: string;
  prescription: PathItem[];
  step: number;
  startedAt: string;
};

/* ── Defensive parse ──────────────────────────────────────
 *
 * localStorage strings are user-controlled (private mode, manual
 * edits, partial writes from interrupted sessions). Validate every
 * field we depend on; on any failure, return null and let the badge
 * render nothing rather than crash the nav. */
function parsePathState(raw: string | null): PathState | null {
  if (!raw) return null;
  try {
    const v: unknown = JSON.parse(raw);
    if (!v || typeof v !== "object") return null;
    const obj = v as Record<string, unknown>;
    if (typeof obj.persona !== "string") return null;
    if (typeof obj.personaLabel !== "string") return null;
    if (typeof obj.step !== "number" || !Number.isFinite(obj.step)) return null;
    if (typeof obj.startedAt !== "string") return null;
    if (!Array.isArray(obj.prescription)) return null;
    const prescription: PathItem[] = [];
    for (const it of obj.prescription) {
      if (!it || typeof it !== "object") return null;
      const r = it as Record<string, unknown>;
      if (typeof r.title !== "string" || typeof r.href !== "string") {
        return null;
      }
      const item: PathItem = { title: r.title, href: r.href };
      if (typeof r.minutes === "number" && Number.isFinite(r.minutes)) {
        item.minutes = r.minutes;
      }
      if (typeof r.external === "boolean") {
        item.external = r.external;
      }
      prescription.push(item);
    }
    if (prescription.length === 0) return null;
    return {
      persona: obj.persona,
      personaLabel: obj.personaLabel,
      prescription,
      step: Math.max(0, Math.floor(obj.step)),
      startedAt: obj.startedAt,
    };
  } catch {
    return null;
  }
}

function readPath(): PathState | null {
  try {
    return parsePathState(window.localStorage.getItem(PATH_KEY));
  } catch {
    return null;
  }
}

function writePath(p: PathState): void {
  try {
    window.localStorage.setItem(PATH_KEY, JSON.stringify(p));
    // Same-tab listeners don't fire `storage` for their own writes,
    // so dispatch a synthetic event for in-page subscribers (the
    // badge itself, future overlays).
    window.dispatchEvent(
      new StorageEvent("storage", {
        key: PATH_KEY,
        newValue: JSON.stringify(p),
      }),
    );
  } catch {
    /* private mode / quota — ignore. */
  }
}

function clearPath(): void {
  try {
    window.localStorage.removeItem(PATH_KEY);
    window.dispatchEvent(
      new StorageEvent("storage", { key: PATH_KEY, newValue: null }),
    );
  } catch {
    /* ignore. */
  }
}

export default function PathBadge() {
  const [path, setPath] = useState<PathState | null>(null);
  const [, navigate] = useLocation();
  const [narrow, setNarrow] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 720px)").matches;
  });

  // Initial read + cross-tab + same-tab updates. Same-tab writes from
  // About.tsx don't dispatch synthetic events, so we also poll on
  // window focus to catch a quiz completion that happened in this tab
  // before mount or in a sibling React tree branch.
  useEffect(() => {
    setPath(readPath());
    const onStorage = (e: StorageEvent) => {
      if (e.key && e.key !== PATH_KEY) return;
      setPath(readPath());
    };
    const onFocus = () => setPath(readPath());
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 720px)");
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    // Older Safari fallback — still typed.
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  const advance = useCallback(() => {
    if (!path) return;
    const nextStep = path.step + 1;
    const target = path.prescription[path.step];
    const updated: PathState = { ...path, step: nextStep };
    writePath(updated);
    setPath(updated);
    if (target) {
      if (target.external) {
        // External — open in a new tab; don't change route here.
        try {
          window.open(target.href, "_blank", "noopener,noreferrer");
        } catch {
          /* popup blocked — navigation still recorded. */
        }
      } else {
        navigate(target.href);
      }
    }
  }, [path, navigate]);

  if (!path) return null;

  const total = path.prescription.length;
  const isComplete = path.step >= total;
  const current = isComplete ? null : path.prescription[path.step] ?? null;

  // ── Complete state ───────────────────────────────────
  if (isComplete) {
    return (
      <div
        aria-label="Reading path complete"
        title={`${path.personaLabel} · path complete`}
        style={badgeShellStyle}
      >
        {!narrow && (
          <span style={kickerStyle}>your path</span>
        )}
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "var(--accent-2)",
            boxShadow: "0 0 0 2px var(--accent-2-soft)",
          }}
        />
        {!narrow && (
          <span style={{ color: "var(--ink)", fontSize: 11, letterSpacing: 0.4 }}>
            complete ✓
          </span>
        )}
        <button
          type="button"
          onClick={clearPath}
          aria-label="Dismiss completed path"
          style={dismissButtonStyle}
        >
          ×
        </button>
      </div>
    );
  }

  // ── Mid-path state ───────────────────────────────────
  // current is non-null here (isComplete guarded above), but TS likes
  // an explicit narrow.
  if (!current) return null;

  const stepLabel = `${path.step + 1}/${total}`;

  if (narrow) {
    // Mobile collapsed register — single tap target that advances and
    // navigates. aria-label carries the full context for AT users.
    return (
      <button
        type="button"
        onClick={advance}
        aria-label={`Continue your path: ${current.title} (step ${stepLabel})`}
        title={`${path.personaLabel} · ${current.title}`}
        style={{
          ...badgeShellStyle,
          gap: 6,
          padding: "4px 10px",
          cursor: "pointer",
        }}
      >
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "var(--accent)",
          }}
        />
        <span
          style={{
            color: "var(--ink)",
            fontSize: 11,
            letterSpacing: 0.4,
          }}
        >
          →
        </span>
      </button>
    );
  }

  // Desktop register — kicker + persona + arrow + title + next link.
  // The title is a Link to the current item so a reader who's mid-step
  // and lands on a different page can return to the in-progress item
  // without advancing. The "next →" affordance advances + navigates.
  const titleNode = current.external ? (
    <a
      href={current.href}
      target="_blank"
      rel="noreferrer"
      style={titleLinkStyle}
      title={`Open: ${current.title}`}
    >
      {current.title}
    </a>
  ) : (
    <Link href={current.href} style={titleLinkStyle} title={`Open: ${current.title}`}>
      {current.title}
    </Link>
  );

  return (
    <div
      aria-label={`Your reading path: step ${stepLabel}, ${current.title}`}
      style={badgeShellStyle}
    >
      <span style={kickerStyle}>your path</span>
      <span style={dotStyle} aria-hidden />
      <span
        style={{
          color: "var(--ink-2)",
          fontSize: 11,
          letterSpacing: 0.3,
          textTransform: "lowercase",
          maxWidth: 140,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={path.personaLabel}
      >
        {path.personaLabel}
      </span>
      <span style={{ color: "var(--ink-3)", fontSize: 11 }} aria-hidden>
        →
      </span>
      <span
        style={{
          maxWidth: 200,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {titleNode}
      </span>
      <span style={{ color: "var(--ink-3)", fontSize: 10 }}>{stepLabel}</span>
      <button
        type="button"
        onClick={advance}
        aria-label={`Advance to next path item${
          path.prescription[path.step + 1]
            ? `: ${path.prescription[path.step + 1].title}`
            : " (final)"
        }`}
        style={nextButtonStyle}
      >
        next →
      </button>
    </div>
  );
}

/* ── Shared styles ────────────────────────────────────── */

const badgeShellStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "4px 10px",
  border: "1px solid var(--line)",
  borderLeft: "2px solid var(--accent)",
  background: "var(--panel)",
  fontFamily: "var(--mono)",
  fontSize: 11,
  letterSpacing: 0.4,
  color: "var(--ink)",
  borderRadius: 2,
};

const kickerStyle: React.CSSProperties = {
  fontFamily: "var(--mono)",
  fontSize: 9,
  letterSpacing: 1,
  textTransform: "uppercase",
  color: "var(--ink-3)",
};

const dotStyle: React.CSSProperties = {
  display: "inline-block",
  width: 4,
  height: 4,
  borderRadius: "50%",
  background: "var(--accent)",
};

const titleLinkStyle: React.CSSProperties = {
  color: "var(--ink)",
  textDecoration: "none",
  fontSize: 11,
  letterSpacing: 0.3,
  borderBottom: "1px solid var(--line)",
  paddingBottom: 1,
};

const nextButtonStyle: React.CSSProperties = {
  background: "var(--accent-soft)",
  color: "var(--accent)",
  border: "1px solid var(--accent)",
  fontFamily: "var(--mono)",
  fontSize: 10,
  letterSpacing: 0.6,
  textTransform: "lowercase",
  padding: "3px 8px",
  cursor: "pointer",
  borderRadius: 2,
  marginLeft: 2,
};

const dismissButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: 0,
  color: "var(--ink-3)",
  fontFamily: "var(--mono)",
  fontSize: 14,
  lineHeight: 1,
  padding: "2px 4px",
  cursor: "pointer",
  marginLeft: 2,
};
